import { getTimestamp } from 'common/utils';
import { Action, CashMovement, CashRegisterState } from 'common/types';
import { db } from 'firebase/config';
import { setDocWithCreatedBy } from 'firebase/utils';
import { collection, doc, addDoc, updateDoc, getDocs, query, where, orderBy, limit, CollectionReference, DocumentData, onSnapshot } from 'firebase/firestore';

const cashRegisterCollection: CollectionReference<DocumentData> = collection(db, 'cashRegister');
const cashMovementsCollection: CollectionReference<DocumentData> = collection(db, 'cashMovements');

export type CashMovementsActions = {
  fetchCashRegisters: () => Promise<CashRegisterState[]>;
  fetchCashMovements: (registerId: string) => Promise<CashMovement[]>;
  selectRegister: (registerId: string | null) => void;
};

export const createCashMovementsActions: Action<CashMovementsActions> = (state, updateState) => ({
  // Fetch cash registers (closed and open)
  fetchCashRegisters: async () => {
    try {
      const q = query(
        cashRegisterCollection,
        orderBy('openDate', 'desc'),
        limit(20) // Limit to last 20 registers
      );

      const querySnapshot = await getDocs(q);
      const registers: CashRegisterState[] = [];
      
      querySnapshot.forEach((doc) => {
        const registerData = { id: doc.id, ...doc.data() } as CashRegisterState;
        registers.push(registerData);
      });

      updateState({ cashRegisters: registers });
      
      // Select the most recent register by default if none is selected
      if (registers.length > 0 && !state.selectedCashRegisterId) {
        updateState({ selectedCashRegisterId: registers[0].id });
      }
      
      return registers;
    } catch (err) {
      console.error('Error fetching cash registers:', err);
      throw err;
    }
  },

  // Fetch cash movements for a specific register
  fetchCashMovements: async (registerId: string) => {
    try {
      const selectedRegisterData = state.cashRegisters.find(register => register.id === registerId);
      
      if (!selectedRegisterData) {
        throw new Error('Register not found');
      }

      let movementsQuery;
      
      if (selectedRegisterData.closeDate) {
        // If register is closed, get movements between open and close dates
        movementsQuery = query(
          cashMovementsCollection,
          where('date', '>=', selectedRegisterData.openDate),
          where('date', '<=', selectedRegisterData.closeDate),
          orderBy('date', 'asc')
        );
      } else {
        // If register is still open, get all movements since open date
        movementsQuery = query(
          cashMovementsCollection,
          where('date', '>=', selectedRegisterData.openDate),
          orderBy('date', 'asc')
        );
      }

      const movementsSnapshot = await getDocs(movementsQuery);
      
      const movements: CashMovement[] = [];
      let totalAdditions = 0;
      let totalWithdrawals = 0;

      movementsSnapshot.forEach((doc) => {
        const movement = { id: doc.id, ...doc.data() } as CashMovement;
        movements.push(movement);
      });

      updateState({ cashMovements: movements });
      return movements;
    } catch (err) {
      console.error('Error fetching cash movements:', err);
      throw err;
    }
  },

  // Select a cash register
  selectRegister: (registerId: string | null) => {
    updateState({ selectedCashRegisterId: registerId });
  },
});
